<template>
  <div>
    <b-overlay :show="showOverlay">
      <ValidationObserver ref="searchForm" slim>
        <b-form @submit.prevent="submit">
          <!-- Search form component - can be split into sub-components (such as Room) -->
          <b-col md="12" class="py-3 bg-light border mb-3 rounded">
            <b-row>
              <b-col><h4 class="view-name"><translate>Hotel</translate></h4></b-col>
            </b-row>
            <b-row>
              <b-col md="3">
                <ValidationProvider
                  name="Currency"
                  :rules="{ required: true }"
                  v-slot="vContext"
                >
                  <b-form-group
                    :label="$gettext('Currency')"
                    label-class="font-weight-bold my-0 py-0"
                    label-for="currency"
                  >
                    <b-form-select
                      id="currency"
                      size="sm"
                      v-model="selectedCurrency"
                      :options="currencyOptions"
                      :state="getValidationState(vContext)"
                      aria-describedby="currency"
                      ></b-form-select>
                      <b-form-invalid-feedback id="currency">{{ vContext.errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col md="3">
                <ValidationProvider
                  name="Destination Country"
                  :rules="{ required: true }"
                  v-slot="vContext"
                >
                  <b-form-group
                    :label="$gettext('Destination Country')"
                    label-class="font-weight-bold my-0 py-0"
                    label-for="country"
                  >
                    <b-form-select
                      id="country"
                      size="sm"
                      v-model="selectedCountry"
                      :options="countryOptions"
                      :state="getValidationState(vContext)"
                      aria-describedby="country"
                      >
                        <template v-slot:first>
                          <b-form-select-option :value="null" disabled v-translate>-- select --</b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-form-invalid-feedback id="country">{{ vContext.errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col md="3">
                <ValidationProvider
                  name="Destinatie"
                  :rules="{ required: true }"
                  v-slot="vContext"
                >
                  <b-form-group
                    :label="$gettext('Resort')"
                    label-class="font-weight-bold my-0 py-0"
                    label-for="resort"
                  >
                    <b-form-select
                      id="resort"
                      size="sm"
                      v-model="selectedResort"
                      :options="resortOptions"
                      :disabled="!selectedCountry"
                      :state="getValidationState(vContext)"
                      aria-describedby="resort"
                      >
                        <template v-slot:first>
                          <b-form-select-option :value="null" disabled v-translate>-- select --</b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-form-invalid-feedback id="resort">{{ vContext.errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col md="3">
                <b-form-group
                  :label="$gettext('Area')"
                  label-class="font-weight-bold my-0 py-0"
                  label-for="area"
                >
                  <b-form-select
                    id="area"
                    size="sm"
                    v-model="selectedArea"
                    :options="areaOptions"
                    :disabled="!selectedResort"
                    aria-describedby="area"
                    >
                      <template v-slot:first>
                        <b-form-select-option :value="null" v-translate>-- any --</b-form-select-option>
                      </template>
                  </b-form-select>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="3">
                <b-form-group
                  :label="$gettext('Hotel')"
                  label-class="font-weight-bold my-0 py-0"
                  label-for="hotel"
                >
                  <b-form-select
                    id="hotel"
                    size="sm"
                    v-model="selectedHotel"
                    :options="hotelOptions"
                    :disabled="!selectedResort"
                    aria-describedby="hotel"
                    >
                      <template v-slot:first>
                        <b-form-select-option :value="null" v-translate>-- any --</b-form-select-option>
                      </template>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group
                  :label="$gettext('Category')"
                  label-class="font-weight-bold my-0 py-0"
                  label-for="category"
                >
                  <b-form-select
                    id="category"
                    size="sm"
                    v-model="selectedCategory"
                    :options="categoryOptions"
                    >
                      <template v-slot:first>
                        <b-form-select-option :value="null" v-translate>-- any --</b-form-select-option>
                      </template>
                    </b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <ValidationProvider
                  name="Check in"
                  :rules="{ required: true }"
                  vid="checkIn"
                  v-slot="vContext"
                  >
                  <b-form-group
                    :label="$gettext('Check in')"
                    label-class="font-weight-bold my-0 py-0"
                    label-for="checkIn">
                    <DatePicker
                      v-model="selectedCheckIn"
                      id="checkIn"
                      :state="getValidationState(vContext)"
                      size="sm"
                      :placeholder="$gettext('dd/mm/yyyy')"
                      lang="en"
                      :today-label="$gettext('today')"
                      :selected-date-label="$gettext('selected date')"
                      class="hp-btn"
                      aria-describedby="checkin-live-feedback"
                      @change="onCheckInChange"
                    ></DatePicker>
                    <b-form-invalid-feedback id="checkin-live-feedback">{{ vContext.errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col md="1">
                <ValidationProvider
                  name="Nopti"
                  :rules="{ required: true }"
                  vid="nights"
                  v-slot="vContext">
                  <b-form-group
                    :label="$gettext('Nights')"
                    label-class="font-weight-bold my-0 py-0"
                    label-for="nights">
                    <b-input
                      v-model="nights"
                      size="sm"
                      aria-describedby="nights-live-feedback" />
                    <b-form-invalid-feedback id="nights-live-feedback">{{ vContext.errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col md="3">
                <ValidationProvider
                  name="Check out"
                  label-class="font-weight-bold my-0 py-0"
                  label-for="checkOut"
                  :rules="{ required: true }"
                  vid="checkOut"
                  v-slot="vContext"
                  >
                  <b-form-group
                    :label="$gettext('Check out')"
                    label-class="font-weight-bold my-0 py-0"
                    label-for="checkOut"
                    >
                    <DatePicker
                      v-model="selectedCheckOut"
                      id="checkOut"
                      :state="getValidationState(vContext)"
                      size="sm"
                      lang="en"
                      :today-label="$gettext('today')"
                      :selected-date-label="$gettext('selected date')"
                      class="hp-btn"
                      :placeholder="$gettext('dd/mm/yyyy')"
                      aria-describedby="checkout-live-feedback"
                      @change="onCheckOutChange"
                    ></DatePicker>
                    <b-form-invalid-feedback id="checkout-live-feedback">{{ vContext.errors[0] }}</b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="3" v-for="(room, i) in selectedParams.rooms" :key="genRoomKey(i)">
                <b-form-group
                  :label="$gettext('Room') + ' ' + (i + 1)"
                  label-class="font-weight-bold my-0 py-0 form-label"
                >
                  <room
                    v-model="selectedParams.rooms[i]"
                    :id="i"
                    :showDelete="i && (selectedParams.rooms.length > 1)"
                    :showAdd = "!i && (selectedParams.rooms.length < 3)"
                    @add = "onRoomAdd"
                    @delete = "onRoomDelete(i)">
                  </room>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col  sm="12" class="d-flex align-items-center justify-content-end pt-2">
                <b-button variant="success" size="sm" type="submit"><b-icon icon="search" /><span class="ml-2" v-translate>Search</span></b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-form>
      </ValidationObserver>
    </b-overlay>
   </div>
</template>

<script>
import moment from 'moment'
import formMixin from '@/app/private/shared/mixins/search_forms'
import DatePicker from 'b2b-common/components/datepicker'
import room from '@/app/private/shared/components/search_form_room'
import { currencies } from '@/shared/utils'
import { hotelRatings } from 'b2b-common/utils/hotel'
import { getCountries, getResorts, getAreas, getHotels } from 'b2b-common/services/api/hotel'
import validation from 'b2b-common/mixins/validation'
import toast from 'b2b-common/mixins/toast'

export default {
  name: 'searchForm',
  components: { DatePicker, room },
  mixins: [formMixin, validation, toast],
  props: ['params'],
  data () {
    return {
      selectedParams: {
        currency: null,
        country: null,
        resort: null,
        area: null,
        hotel: null,
        category: null,
        checkIn: null,
        checkOut: null
      },
      countriesList: [],
      resortsList: [],
      areasList: [],
      hotelsList: [],
      defaultNights: 7
    }
  },
  methods: {
    submit () {
      this.$refs.searchForm.validate().then(success => {
        if (!success) {
          this.addToast({
            title: this.$gettext('Invalid form'),
            message: this.$gettext('Please fill in all required fields!'),
            variant: 'danger'
          })
        } else {
          this.onSubmit()
        }
      })
    },
    updateCheckIn (date) {
      this.selectedCheckIn = date
    },
    updateCheckOut (date) {
      this.selectedCheckOut = date
    },
    async getCountriesList () {
      this.resetList('countries')
      this.addRequest('countries')
      try {
        this.countriesList = await getCountries({ currency: this.selectedCurrency })
        if (this.selectedCountry && !this.valueExistsInList('countries', this.selectedCountry)) {
          this.resetList(['resorts', 'areas', 'hotels'])
          this.selectedCountry = null
        }
      } finally { this.removeRequest('countries') }
      return this.countriesList
    },
    async getResortsList () {
      this.resetList('resorts')
      this.addRequest('resorts')
      try {
        let res = []
        if (this.selectedCountry) {
          res = await getResorts({
            currency: this.selectedCurrency,
            country: this.selectedCountry
          })
        }
        this.resortsList = res
        if (this.selectedResort && !this.valueExistsInList('resorts', this.selectedResort)) {
          this.resetList(['areas', 'hotels'])
          this.selectedResort = null
        }
      } finally { this.removeRequest('resorts') }
      return this.resortsList
    },
    async getAreasList () {
      this.resetList('areas')
      this.addRequest('areas')
      try {
        let res = []
        if (this.selectedResort) {
          res = await getAreas({
            currency: this.selectedCurrency,
            resort: this.selectedResort
          })
        }
        this.areasList = res
        if (this.selectedArea && !this.valueExistsInList('areas', this.selectedArea)) {
          this.resetList('hotels')
          this.selectedArea = null
        }
      } finally { this.removeRequest('areas') }
      return this.areasList
    },
    async getHotelsList () {
      this.resetList('hotels')
      this.addRequest('hotels')
      try {
        let res = []
        if (this.selectedResort || this.selectedArea) {
          res = await getHotels({
            currency: this.selectedCurrency,
            destination: this.selectedArea ? this.selectedArea : this.selectedResort
          })
        }
        this.hotelsList = res
        if (this.selectedHotel && !this.valueExistsInList('hotels', this.selectedHotel)) {
          this.selectedHotel = null
        }
      } finally { this.removeRequest('hotels') }
      return this.hotelsList
    },
    onCheckOutChange (val) {
      const mVal = moment(val)
      // check to see if it is a valid value
      // it it's invalid don't do anything
      if (!mVal.isValid()) return
      let mCheckIn = moment(this.selectedCheckIn)
      if (!mCheckIn.isValid() || mCheckIn.isAfter(mVal)) {
        // modify check in according to no of nights
        if (!mCheckIn.isValid()) mCheckIn = moment(mVal).hour(15).minute(0).second(0)
        const currentNights = moment(mCheckIn).startOf('day').diff(moment(mVal).startOf('day'), 'days')
        this.selectedCheckIn = mCheckIn.subtract(currentNights + this.defaultNights, 'days').toDate()
      }
    },
    onCheckInChange (val) {
      const mVal = moment(val)
      // check to see if it is a valid value
      // it it's invalid don't do anything
      if (!mVal.isValid()) return
      let mCheckOut = moment(this.selectedCheckOut)
      // modify check out according to default no of nights
      if (!mCheckOut.isValid()) mCheckOut = moment(mVal).hour(11).minute(0).second(0)
      const currentNights = moment(mVal).startOf('day').diff(moment(mCheckOut).startOf('day'), 'days')
      this.selectedCheckOut = mCheckOut.add(currentNights + this.defaultNights, 'days').toDate()
    }
  },
  computed: {
    selectedCheckIn: {
      get () {
        const mVal = moment(this.selectedParams.checkIn, 'DD-MM-YYYY')
        if (mVal.isValid()) return mVal.toDate()
        return null
      },
      set (val) {
        this.selectedParams.checkIn = val ? moment(val).format('DD-MM-YYYY') : null
      }
    },
    selectedCheckOut: {
      get () {
        const mVal = moment(this.selectedParams.checkOut, 'DD-MM-YYYY')
        if (mVal.isValid()) return mVal.toDate()
        return null
      },
      set (val) {
        this.selectedParams.checkOut = val ? moment(val).format('DD-MM-YYYY') : null
      }
    },
    nights: {
      get () {
        if (this.selectedParams.checkIn && this.selectedParams.checkOut) {
          return moment(this.selectedCheckOut).startOf('day').diff(moment(this.selectedCheckIn).startOf('day'), 'days')
        }
        return this.defaultNights
      },
      set (val) {
        if (val === null || val === undefined || val === '' || val < 0) return
        this.defaultNights = val
        if (this.selectedCheckIn) {
          const start = moment(this.selectedCheckIn)
          const end = (this.selectedCheckOut) ? moment(this.selectedCheckOut) : moment(start).set('hour', 11).set('minute', 0)
          const eodStart = moment(start).startOf('day')
          const eodEnd = moment(end).startOf('day')
          const currentNights = eodEnd.diff(eodStart, 'days')
          this.selectedCheckOut = end.add(val - currentNights, 'days').toDate()
        }
      }
    },
    selectedCurrency: {
      get () { return this.selectedParams.currency },
      async set (val) {
        this.selectedParams.currency = val
        await this.getCountriesList()
        await this.getResortsList()
        await this.getAreasList()
        this.getHotelsList()
      }
    },
    currencyOptions () {
      return currencies.map(el => ({ value: el.code, text: el.code }))
    },
    selectedCountry: {
      get () {
        return this.selectedParams.country || null
      },
      async set (val) {
        this.selectedParams.country = val
        await this.getResortsList()
        await this.getAreasList()
        this.getHotelsList()
      }
    },
    countryOptions () {
      return this.countriesList.map(el => ({ value: el.id, text: el.name }))
    },
    selectedResort: {
      get () { return this.selectedParams.resort || null },
      async set (val) {
        this.selectedParams.resort = val
        await this.getAreasList()
        this.getHotelsList()
      }
    },
    resortOptions () {
      return this.resortsList.map(el => ({ value: el.id, text: el.name }))
    },
    selectedArea: {
      get () { return this.selectedParams.area },
      set (val) {
        this.selectedParams.area = val
        this.getHotelsList()
      }
    },
    areaOptions () {
      return this.areasList.map(el => ({ value: el.id, text: el.name }))
    },
    selectedHotel: {
      get () { return (Array.isArray(this.selectedParams.hotels) && this.selectedParams.hotels.length) ? this.selectedParams.hotels[0] : null },
      set (val) { this.selectedParams.hotels = val ? [val] : null }
    },
    hotelOptions () {
      return this.hotelsList.map(el => ({ value: el.id, text: el.name }))
    },
    selectedCategory: {
      get () { return this.selectedParams.category },
      set (val) { this.selectedParams.category = val }
    },
    categoryOptions () {
      return hotelRatings.map(el => ({ value: el, text: el + ' ' + this.$gettext('stars') }))
    },
    showOverlay () { return this.activeRequests.length > 0 }
  },
  created () {
    this.selectedParams = { ...this.params }
    this.getCountriesList()
    this.getResortsList()
    this.getAreasList()
    this.getHotelsList()
  }
}
</script>

<style>
  .datepicker__wrapper.datePiker {
    height: 30px!important;
  }
  .datepicker__wrapper.datePiker > .datepicker__dummy-wrapper{
    border-color: #ccc;
    border-radius: 4px;
  }
  .datepicker__wrapper.datePiker > .datepicker__dummy-wrapper .datepicker__input {
    height: 31px!important;
    line-height: 2.1!important;
  }
  .datePiker .datepicker__clear-button {
    margin: -1px -2px 0 0;
  }
</style>
